/**
 * @file main.ts
 * @role Entry point. Finds the host element and runs the Bootstrapper (the single
 *       composition root). Nothing else lives here — wiring belongs in Bootstrapper.
 * @owns Locating #game-root and kicking off boot.
 * @neverDoes Construct or wire services directly.
 * @see docs/ARCHITECTURE.md, src/app/Bootstrapper.ts
 */
import '@app/pixiExtensions';
import { Bootstrapper } from '@app/Bootstrapper';
import { showBootShellError } from '@app/BootShell';
import { startRgsMockWorker } from './mocks/startRgsMockWorker';

const host = document.getElementById('game-root');
if (!host) {
  throw new Error('Missing #game-root host element in index.html');
}

void (async () => {
  try {
    // Opt-in dev fake RGS (VITE_RGS_MODE=msw). Awaited so it is intercepting before the
    // Bootstrapper's first session request; a no-op in every other build.
    const mockedRgs = await startRgsMockWorker();
    if (mockedRgs) console.info(`[rgs-mock] intercepting ${mockedRgs}`);
    await new Bootstrapper(host).run();
  } catch (error: unknown) {
    console.error('[boot] failed to start game', error);
    // Terminal boot failure: never leave the animated shell pulsing forever (Invariant 21).
    // A full reload is the only safe retry this early — no services are guaranteed alive.
    showBootShellError('Failed to start the game. Check your connection and retry.', () =>
      window.location.reload(),
    );
  }
})();
